import api from './api';

export type CampaignStatus = 'rascunho' | 'agendada' | 'ativa' | 'pausada' | 'terminada';
export type CampaignType =
  | 'lancamento'
  | 'cashback'
  | 'referral'
  | 'evento'
  | 'parceiro'
  | 'conteudo';

export type CampaignChannel = 'app' | 'push' | 'email' | 'sms' | 'social' | 'offline';

export interface Campaign {
  id: number;
  nome: string;
  slug: string;
  tipo: CampaignType;
  status: CampaignStatus;
  descricao: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  orcamento_cve: number | null;
  canais: CampaignChannel[];
  audiencia: string | null;
  kpi_alvo: string | null;
  metrica_principal: string | null;
  valor_atingido: number | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignStats {
  total: number;
  ativas: number;
  agendadas: number;
  terminadas: number;
  orcamento_total: number;
  cashback_distribuido: number;
  utilizadores_impactados: number;
}

export interface ListCampaignsParams {
  status?: CampaignStatus;
  tipo?: CampaignType;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CampaignPayload {
  nome: string;
  tipo: CampaignType;
  status?: CampaignStatus;
  descricao?: string;
  data_inicio?: string;
  data_fim?: string;
  orcamento_cve?: number;
  canais?: CampaignChannel[];
  audiencia?: string;
  kpi_alvo?: string;
  metrica_principal?: string;
}

class MarketingService {
  async getCampaignStats(): Promise<CampaignStats> {
    const res = await api.get<{ success: boolean; data?: CampaignStats }>(
      '/admin/marketing/campaigns/stats'
    );
    return (
      res.data.data || {
        total: 0,
        ativas: 0,
        agendadas: 0,
        terminadas: 0,
        orcamento_total: 0,
        cashback_distribuido: 0,
        utilizadores_impactados: 0,
      }
    );
  }

  async listCampaigns(params?: ListCampaignsParams): Promise<Campaign[]> {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.tipo) qs.append('tipo', params.tipo);
    if (params?.search) qs.append('search', params.search);
    if (params?.limit != null) qs.append('limit', String(params.limit));
    if (params?.offset != null) qs.append('offset', String(params.offset));
    const res = await api.get<{ success: boolean; data?: Campaign[] }>(
      `/admin/marketing/campaigns?${qs.toString()}`
    );
    return res.data.data || [];
  }

  async getCampaign(id: number): Promise<Campaign> {
    const res = await api.get<{ success: boolean; data: Campaign }>(
      `/admin/marketing/campaigns/${id}`
    );
    return res.data.data;
  }

  async createCampaign(payload: CampaignPayload): Promise<Campaign> {
    const res = await api.post<{ success: boolean; data: Campaign }>(
      '/admin/marketing/campaigns',
      payload
    );
    return res.data.data;
  }

  async updateCampaign(id: number, payload: Partial<CampaignPayload>): Promise<Campaign> {
    const res = await api.put<{ success: boolean; data: Campaign }>(
      `/admin/marketing/campaigns/${id}`,
      payload
    );
    return res.data.data;
  }

  async updateStatus(id: number, status: CampaignStatus): Promise<void> {
    await api.post(`/admin/marketing/campaigns/${id}/status`, { status });
  }

  async deleteCampaign(id: number): Promise<void> {
    await api.delete(`/admin/marketing/campaigns/${id}`);
  }
}

export default new MarketingService();
